import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import { graphql, Link, StaticQuery } from 'gatsby'
import media from 'css-in-js-media'

import Container from './Container'
import SelectLanguage, { LanguageLinks } from './SelectLanguage'
import { Row, Col } from 'react-styled-flexboxgrid'
import Grid from './Grid'
import { BodyCopy } from './Typography'
import { Variables } from '../styles/Variables'
import { useIntl } from 'react-intl'
import LayoutContext from './LayoutContext'
import { DesktopView, MobileView } from '../styles/Utils'

const Andermatt_logo = require('../svg/andermatt.png') as string

const FooterWrapper = styled.div`
    border-top: 1px solid ${Variables.color.white};
    background-color: ${Variables.color.cocoa};
    padding-top: 40px;
    padding-bottom: 60px;
    color: ${Variables.color.white};
    font-family: ${Variables.font.button};

    ${media('<desktop')} {
        text-align: center;
        padding-top: 38px;
        padding-bottom: 54px;
    }

    a {
        color: ${Variables.color.white};
    }
`

const Copyright = styled.div`
    font-family: ${Variables.font.body};
    font-weight: 600;
    font-size: 16px;
    line-height: 130%;    
    letter-spacing: 0.1em;
    text-transform: uppercase;    

    ${media('<desktop')} {            
        font-size: 14px;                    
        line-height: 100%;
    }

    &:before {
        content: '© ';
        font-family: 'Helvetica', 'Arial', sans-serif;
        font-weight: bold;
    }
`

const FooterCopy = styled.div`
    padding-bottom: 36px;
    ${media('>desktop')} {
        padding-bottom: 78px;
    }

    ${BodyCopy} {
        font-family: ${Variables.font.button};
        color: ${Variables.color.white};
        font-size: 16px;
        line-height: 25px;

        ${media('<desktop')} {
            line-height: 28px;
            letter-spacing: 0.005em;
        }
    }
`

const LogoWrap = styled.div`
    text-align: center;
    ${media('<desktop')} {
        width: 100%;
    }
`

const Logo = styled.div`    
    ${media('<desktop')} {
        padding-top: 36px;
    }

    img {
        height: 86px;

        ${media('<desktop')} {
            height: 55px;
        }
    }

    a:hover {
        opacity: 0.8;
    }
`

const Links = styled.div`
    a {
        text-transform: uppercase;
        font-size: 16px;
        line-height: 160%;
        font-weight: 500;
        letter-spacing: 0.1em;

        border-bottom: 1px solid transparent;
        transition: 0.1s border-color ease-out;

        :hover {
            border-color: ${Variables.color.lightGrey};
        }

        ${media('<desktop')} {
            font-size: 16px;
            line-height: 28px;
        }
    }

    ${(props) =>
        props.lang == 'de' &&
        css`
            ${media('<desktop')} {
                padding-right: 15px;
            }
        `}
`

const LinksWrap = styled(Row)`
    justify-content: center; 

    ${Col} {
        ${media('<desktop')} {
            width: 100%;
        }
    }
`

const FooterDesc = styled.div`
    text-align: center;
    padding-bottom: 16px;

    ${media('<desktop')} {
        padding-bottom: 38px;
    }

    ${BodyCopy} {
        color: ${Variables.color.white};
        font-family: ${Variables.font.body};
        font-size: 20px;
        line-height: 120%;        
        ${media('<desktop')} {            
            font-size: 16px;
        }
    }
`

const FooterLanguage = styled(Col)<{$padding?: boolean}>`
    ${props => props.$padding && css`
        padding-bottom: 30px;        
    `}
    ${media('>desktop')} {
        padding-bottom: 78px;
    }
    ${LanguageLinks} {
        justify-content: end;        
    }    
    header {
        display: none;
    }

`

const FooterTop = styled(Row)`
    padding-bottom: 38px;
    ${media('<desktop')} {
        padding-bottom: 20px;
    }
`;

export const FooterContainer: React.FC = ({ data }) => {
    const { currentLang, languageData } = useContext(LayoutContext)
    const langPrefix = currentLang.prefix
    const langKeyShort = currentLang.langKeyShort
    const Footerdata = data.allContentfulFooter.edges.find((i) => i.node.node_locale == currentLang.langKey).node

    const intl = useIntl()

    return (
        <FooterWrapper>
            <Grid fluid={true}>
                <Container>                    
                    <FooterTop between="sm" middle="sm">                                         
                        <Col xs={12} md={4}>   
                            <FooterCopy>
                                <Copyright>{Footerdata.copyright}</Copyright>                                
                            </FooterCopy>      
                            <MobileView>
                                <FooterLanguage xs={12} md={12} $padding={false}>
                                    <SelectLanguage style={{justifyContent: 'center', margin: 0}} langs={languageData} />
                                </FooterLanguage>                    
                            </MobileView>
                        </Col>
                        <Col xs={12} md={4}>
                            <LogoWrap>
                                <Logo>
                                    <a
                                        href={`https://andermatt-swissalps.ch/${langKeyShort}`}
                                        target="_blank"
                                    >
                                        <img src={Andermatt_logo} alt="" />
                                    </a>
                                </Logo>
                            </LogoWrap>
                        </Col>
                        <FooterLanguage xs={12} md={4}>
                            <DesktopView>
                                <SelectLanguage langs={languageData} />
                            </DesktopView>
                        </FooterLanguage>       
                    </FooterTop>
                    <Row center="sm" >
                        <Col xs={12} md={8}>
                            <FooterDesc>                                
                                <BodyCopy
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            Footerdata.footerCopy
                                                .childMarkdownRemark.html,
                                    }}
                                    small={true}
                                ></BodyCopy>
                            </FooterDesc>                                  
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={true}>
                            <LinksWrap>
                                <Col>
                                    <Links lang={langPrefix}>
                                        <Link
                                            to={intl.formatMessage({
                                                id: 'privacyLink',
                                            })}
                                            target="_blank"
                                        >
                                            {intl.formatMessage({
                                                id: 'Privacy Policy',
                                            })}
                                        </Link>
                                    </Links>
                                </Col>                                
                            </LinksWrap>
                        </Col>
                    </Row>
                </Container>
            </Grid>
        </FooterWrapper>
    )
}

const Footer = (props) => (
    <StaticQuery
        query={graphql`
            query footerQuery {
                allContentfulFooter(
                    filter: { property: { name: { regex: "/pazola/i" } } }
                ) {
                    edges {
                        node {
                            copyright
                            footerCopy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            node_locale
                        }
                    }
                }
            }
        `}
        render={(data) => <FooterContainer data={data} {...props} />}
    />
)

export default Footer
